import { useEffect, useState } from "react";
import { KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TABS_DEF = [
  { key: "attendance", label: "الحضور والغياب", desc: "سجل الدوام كاملاً: الاستيراد والتعديل والحذف" },
  { key: "inquiries", label: "المساءلات", desc: "إنشاء وإرسال وتتبع المساءلات الرسمية" },
  { key: "deductions", label: "قرارات الحسم", desc: "عرض قرارات الحسم وتعديلها وطباعتها" },
  { key: "circulars", label: "التعاميم", desc: "إصدار التعاميم وتتبع التوقيعات" },
  { key: "reports", label: "التقارير", desc: "تقارير وإحصاءات الحضور مع الطباعة" },
  { key: "employees", label: "الموظفون", desc: "إدارة بيانات الموظفين كاملة" },
  { key: "performance", label: "تقييم الأداء", desc: "تقييم الموظفين واعتماد النماذج والشواهد" },
  { key: "settings", label: "الإعدادات", desc: "أوقات الدوام والعتبات والإجازات والفترات المرنة والترويسة" },
  { key: "extension", label: "إضافة أداة الكروم", desc: "متابعة عمليات الاستيراد ورمز ربط الإضافة" },
];

export default function PermissionsTab() {
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/permissions/employees")
    .then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const open = (e) => { setSel(e); setDraft({ ...(e.permissions || {}) }); };

  const toggle = (key, on) => setDraft((d) => {
    const n = { ...d };
    if (on) { n[key] = true; } else { delete n[key]; }
    return n;
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/permissions/employees/${sel.id}`, { permissions: draft });
      toast.success("حُفظت الصلاحيات بنجاح — تسري على الموظف فوراً");
      setSel(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="permissions-tab">
      <div className="bg-white rounded-2xl border p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-900 flex items-center justify-center shrink-0">
          <KeyRound className="w-5 h-5 text-amber-400" />
        </div>
        <div>
          <h2 className="font-extrabold text-emerald-950 text-base">إدارة الصلاحيات</h2>
          <p className="text-xs text-slate-500">
            فوّض أي موظف لاستخدام تبويبات النظام بنفس شاشة المدير وإمكاناتها من بوابته — تُحفظ وتسري فوراً
          </p>
        </div>
      </div>

      {rows === null && <p className="text-center text-sm text-slate-400 py-10">جارٍ التحميل...</p>}
      {rows?.length === 0 && <p className="text-center text-sm text-slate-400 py-10">لا يوجد موظفون مسجلون</p>}

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {rows?.map((e) => {
          const granted = Object.keys(e.permissions || {});
          return (
            <div key={e.id} className="bg-white rounded-2xl border p-4 space-y-2" data-testid={`perm-employee-${e.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-bold text-sm text-slate-800 truncate">{e.name}</p>
                  <p className="text-[11px] text-slate-400">{e.current_job || "موظف"}</p>
                </div>
                <span className={`text-[10px] font-bold rounded-full border px-2 py-0.5 shrink-0 ${
                  granted.length ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                                 : "bg-slate-50 text-slate-400 border-slate-200"}`}
                  data-testid={`perm-count-${e.id}`}>
                  {granted.length ? `${granted.length} تبويبات` : "بدون صلاحيات"}
                </span>
              </div>
              {granted.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {granted.map((tab) => (
                    <span key={tab} className="text-[10px] bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">
                      {TABS_DEF.find((t) => t.key === tab)?.label}
                    </span>
                  ))}
                </div>
              )}
              <Button variant="outline" size="sm" onClick={() => open(e)}
                data-testid={`btn-edit-perms-${e.id}`}
                className="w-full h-9 text-xs font-bold border-emerald-300 text-emerald-900 hover:bg-emerald-50 gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" /> إدارة الصلاحيات
              </Button>
            </div>
          );
        })}
      </div>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="perm-dialog" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-emerald-950">صلاحيات: {sel?.name}</DialogTitle>
            <DialogDescription className="text-xs">
              فعّل التبويبات التي تريد تفويضها — سيرى الموظف في بوابته شاشة مطابقة لشاشتك بكل إمكاناتها
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {TABS_DEF.map((t) => (
              <div key={t.key} className="rounded-xl border p-3 flex items-center gap-3" data-testid={`perm-row-${t.key}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-slate-800">{t.label}</p>
                  <p className="text-[11px] text-slate-400">{t.desc}</p>
                </div>
                <Switch checked={!!draft[t.key]} onCheckedChange={(v) => toggle(t.key, v)}
                  data-testid={`perm-toggle-${t.key}`} dir="ltr" />
              </div>
            ))}
          </div>
          <Button onClick={save} disabled={saving} data-testid="btn-save-perms"
            className="w-full h-11 bg-emerald-900 hover:bg-emerald-950 text-white font-bold">
            {saving ? "جارٍ الحفظ..." : "حفظ الصلاحيات"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
